import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ChevronLeft, Send, Mic, MicOff, Coins } from 'lucide-react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fake chat messages for simulation
const fakeMessages = [
    "What a save! 🧤",
    "Goooal!! ⚽🔥",
    "Ref is blind! 😤",
    "This match is insane!",
    "Best game of the season!",
    "Come on! Push forward! 💪",
    "That tackle was brutal!",
    "Offside? No way!",
    "YESSS! GET IN! 🎉",
    "The atmosphere is electric!",
    "What a cross! Beautiful!",
    "Defense looking solid 🛡️",
    "Need a substitution ASAP",
    "This ref needs glasses 👓",
    "Penalty! That's a clear penalty!",
    "Hat-trick incoming? 🎩",
    "The crowd is going wild!",
    "Counter attack! Go go go!",
    "Clean sheet so far 🧹",
];

// Fake users for chat
const fakeUsers = [
    { name: "FootballFan99", avatar: "https://i.pravatar.cc/150?img=11" },
    { name: "SaudiKing", avatar: "https://i.pravatar.cc/150?img=12" },
    { name: "GoalMachine", avatar: "https://i.pravatar.cc/150?img=13" },
    { name: "UltrasFan", avatar: "https://i.pravatar.cc/150?img=14" },
    { name: "MatchdayVibes", avatar: "https://i.pravatar.cc/150?img=15" },
    { name: "TacticsGuru", avatar: "https://i.pravatar.cc/150?img=16" },
    { name: "FalconFC", avatar: "https://i.pravatar.cc/150?img=17" },
    { name: "DesertEagle", avatar: "https://i.pravatar.cc/150?img=18" },
];

// Audio hosts (visual only)
const audioHosts = [
    { id: 'host', name: "MatchHost", avatar: "https://i.pravatar.cc/150?img=31", role: "Host", isSpeaking: true },
    { id: 'expert', name: "CoachAhmed", avatar: "https://i.pravatar.cc/150?img=32", role: "Expert", isSpeaking: false },
    { id: 'analyst', name: "ProScout", avatar: "https://i.pravatar.cc/150?img=33", role: "Analyst", isSpeaking: true },
    { id: 'guest', name: "LegendaryPlayer", avatar: "https://i.pravatar.cc/150?img=34", role: "Guest", isSpeaking: false },
];

const LiveMatchRoom = () => {
    const { matchId } = useParams();
    const navigate = useNavigate();
    const { user, updateUser } = useAuth();
    const [match, setMatch] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [floatingReactions, setFloatingReactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [listenerCount, setListenerCount] = useState(127);
    const [speakingHosts, setSpeakingHosts] = useState(audioHosts);
    
    // Betting state
    const [showBettingModal, setShowBettingModal] = useState(false);
    const [odds, setOdds] = useState(null);
    const [selectedBet, setSelectedBet] = useState(null);
    const [betAmount, setBetAmount] = useState('');
    const [userCoins, setUserCoins] = useState(1000);
    const [placingBet, setPlacingBet] = useState(false);
    
    const chatContainerRef = useRef(null);
    const messageIntervalRef = useRef(null);
    const reactionIdRef = useRef(0);
    const speakingIntervalRef = useRef(null);

    // Fetch match data
    useEffect(() => {
        fetchMatch();
        fetchUserCoins();
    }, [matchId]);

    // Start chat simulation
    useEffect(() => {
        if (match) {
            startChatSimulation();
        }
        return () => {
            if (messageIntervalRef.current) {
                clearInterval(messageIntervalRef.current);
            }
        };
    }, [match]);

    // Simulate speaking animation (visual only)
    useEffect(() => {
        startSpeakingAnimation();
        return () => stopSpeakingAnimation();
    }, []);

    // Update listener count randomly
    useEffect(() => {
        const interval = setInterval(() => {
            setListenerCount(prev => Math.max(100, prev + Math.floor(Math.random() * 7) - 3));
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    // Auto-scroll chat
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages]);

    const fetchMatch = async () => {
        try {
            const response = await axios.get(`${API}/matches`);
            const foundMatch = response.data.find(m => m.id === matchId);
            if (foundMatch) {
                setMatch(foundMatch);
                const initialMessages = Array.from({ length: 5 }, () => generateFakeMessage());
                setMessages(initialMessages);
            }
        } catch (error) {
            console.error('Error fetching match:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchUserCoins = async () => {
        try {
            const response = await axios.get(`${API}/users/${user.id}/coins`);
            setUserCoins(response.data.coins);
        } catch (error) {
            console.error('Error fetching coins:', error);
        }
    };

    const fetchOdds = async () => {
        try {
            const response = await axios.get(`${API}/matches/${matchId}/odds`);
            setOdds(response.data);
        } catch (error) {
            console.error('Error fetching odds:', error);
        }
    };

    const generateFakeMessage = () => {
        const randomUser = fakeUsers[Math.floor(Math.random() * fakeUsers.length)];
        const randomMessage = fakeMessages[Math.floor(Math.random() * fakeMessages.length)];
        return {
            id: Date.now() + Math.random(),
            user: randomUser,
            content: randomMessage,
            timestamp: new Date().toISOString(),
            isBetAnnouncement: false
        };
    };

    const startChatSimulation = () => {
        messageIntervalRef.current = setInterval(() => {
            const newFakeMessage = generateFakeMessage();
            setMessages(prev => [...prev.slice(-50), newFakeMessage]);
        }, Math.random() * 2000 + 1000);
    };

    // Visual speaking animation
    const startSpeakingAnimation = () => {
        if (speakingIntervalRef.current) return;
        
        speakingIntervalRef.current = setInterval(() => {
            setSpeakingHosts(prev => {
                const newHosts = [...prev];
                const randomIndex = Math.floor(Math.random() * newHosts.length);
                newHosts[randomIndex] = {
                    ...newHosts[randomIndex],
                    isSpeaking: !newHosts[randomIndex].isSpeaking
                };
                const anyoneSpeaking = newHosts.some(h => h.isSpeaking);
                if (!anyoneSpeaking) {
                    newHosts[0].isSpeaking = true;
                }
                return newHosts;
            });
        }, 2000);
    };

    const stopSpeakingAnimation = () => {
        if (speakingIntervalRef.current) {
            clearInterval(speakingIntervalRef.current);
            speakingIntervalRef.current = null;
        }
    };

    const handleSendMessage = () => {
        if (!newMessage.trim()) return;

        const userMessage = {
            id: Date.now(),
            user: { name: user.username, avatar: user.avatar },
            content: newMessage.trim(),
            timestamp: new Date().toISOString(),
            isOwn: true,
            isBetAnnouncement: false
        };

        setMessages(prev => [...prev, userMessage]);
        setNewMessage('');
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleReaction = (type) => {
        const newReaction = {
            id: reactionIdRef.current++,
            type,
            x: Math.random() * 60 + 20,
        };
        setFloatingReactions(prev => [...prev, newReaction]);

        setTimeout(() => {
            setFloatingReactions(prev => prev.filter(r => r.id !== newReaction.id));
        }, 2000);
    };

    const getReactionEmoji = (type) => {
        switch (type) {
            case 'heart': return '❤️';
            case 'fire': return '🔥';
            case 'clap': return '👏';
            case 'goal': return '⚽';
            default: return '❤️';
        }
    };

    const openBettingModal = () => {
        fetchOdds();
        setShowBettingModal(true);
        setSelectedBet(null);
        setBetAmount('');
    };

    const handlePlaceBet = async () => {
        if (!selectedBet || !betAmount || parseInt(betAmount) < 10) {
            toast.error('Please select an outcome and enter at least 10 coins');
            return;
        }

        if (parseInt(betAmount) > userCoins) {
            toast.error('Insufficient coins');
            return;
        }

        setPlacingBet(true);
        try {
            const response = await axios.post(`${API}/bets`, {
                match_id: matchId,
                selection: selectedBet,
                amount: parseInt(betAmount),
                odds: odds.odds[selectedBet]
            });

            // Update user coins
            setUserCoins(response.data.remaining_coins);
            
            // Inject bet announcement into chat
            const betAnnouncement = {
                id: Date.now(),
                user: { name: user.username, avatar: user.avatar },
                content: `💰 ${user.username} just bet ${betAmount} Coins on ${response.data.bet.selection_name} to win!`,
                timestamp: new Date().toISOString(),
                isOwn: false,
                isBetAnnouncement: true
            };
            setMessages(prev => [...prev, betAnnouncement]);

            toast.success('Bet placed successfully!');
            setShowBettingModal(false);
            setSelectedBet(null);
            setBetAmount('');
        } catch (error) {
            console.error('Error placing bet:', error);
            toast.error(error.response?.data?.detail || 'Failed to place bet');
        } finally {
            setPlacingBet(false);
        }
    };

    const calculatePotentialWin = () => {
        if (!selectedBet || !betAmount || !odds) return 0;
        return (parseInt(betAmount) * odds.odds[selectedBet]).toFixed(2);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!match) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <p className="text-white/60">Match not found</p>
            </div>
        );
    }

    return (
        <div className="h-screen bg-black flex flex-col overflow-hidden" data-testid="live-match-room">
            {/* Floating Reactions */}
            <AnimatePresence>
                {floatingReactions.map((reaction) => (
                    <motion.div
                        key={reaction.id}
                        initial={{ y: '100vh', x: `${reaction.x}%`, opacity: 1, scale: 1 }}
                        animate={{ y: '-20vh', opacity: 0, scale: 1.5 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 2, ease: 'easeOut' }}
                        className="fixed text-4xl pointer-events-none z-50"
                        style={{ left: `${reaction.x}%` }}
                    >
                        {getReactionEmoji(reaction.type)}
                    </motion.div>
                ))}
            </AnimatePresence>

            {/* Header */}
            <div className="glass border-b border-white/10 flex-shrink-0 z-40">
                <div className="flex items-center justify-between px-4 h-14">
                    <button
                        onClick={() => navigate('/scores')}
                        className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
                        data-testid="back-button"
                    >
                        <ChevronLeft className="w-6 h-6 text-white" />
                    </button>
                    <div className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-live rounded-full live-pulse" />
                        <span className="text-live text-sm font-bold">LIVE</span>
                    </div>
                    {/* Coins Display */}
                    <div className="flex items-center gap-1.5 bg-yellow-500/20 px-3 py-1.5 rounded-full">
                        <Coins className="w-4 h-4 text-yellow-400" />
                        <span className="text-yellow-400 text-sm font-bold">{userCoins}</span>
                    </div>
                </div>
            </div>

            {/* Match Score Banner */}
            <div className="bg-card-bg border-b border-border px-4 py-3 flex-shrink-0">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-surface flex items-center justify-center overflow-hidden">
                            <img 
                                src={match.home_logo} 
                                alt={match.home_team}
                                className="w-6 h-6 object-contain"
                                onError={(e) => {
                                    e.target.src = `https://ui-avatars.com/api/?name=${match.home_team}&background=169239&color=fff&size=32`;
                                }}
                            />
                        </div>
                        <span className="text-white text-sm font-medium">{match.home_team}</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="font-heading text-3xl font-bold text-white">{match.home_score}</span>
                        <span className="text-white/30">-</span>
                        <span className="font-heading text-3xl font-bold text-white">{match.away_score}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-white text-sm font-medium">{match.away_team}</span>
                        <div className="w-8 h-8 rounded-full bg-surface flex items-center justify-center overflow-hidden">
                            <img 
                                src={match.away_logo} 
                                alt={match.away_team}
                                className="w-6 h-6 object-contain"
                                onError={(e) => {
                                    e.target.src = `https://ui-avatars.com/api/?name=${match.away_team}&background=169239&color=fff&size=32`;
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className="text-center mt-1">
                    <span className="text-live text-sm font-heading font-semibold">{match.minute}'</span>
                    <span className="text-white/50 text-xs ml-2">{match.league}</span>
                </div>
            </div>

            {/* Audio Space Section */}
            <div className="bg-gradient-to-b from-primary/20 to-transparent px-4 py-4 flex-shrink-0" data-testid="audio-space">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <Mic className="w-4 h-4 text-primary" />
                        <span className="text-white text-sm font-medium">Live Discussion</span>
                    </div>
                    <span className="text-white/50 text-xs">{listenerCount} listening</span>
                </div>
                
                <div className="flex items-center justify-center gap-4">
                    {speakingHosts.map((host) => (
                        <div key={host.id} className="flex flex-col items-center">
                            <div className={`relative ${host.isSpeaking ? 'speaking-ring' : ''}`}>
                                <Avatar className={`w-14 h-14 border-2 transition-all duration-300 ${
                                    host.isSpeaking ? 'border-primary shadow-lg shadow-primary/50' : 'border-card-bg'
                                }`}>
                                    <AvatarImage src={host.avatar} alt={host.name} />
                                    <AvatarFallback className="bg-primary text-white">
                                        {host.name.charAt(0)}
                                    </AvatarFallback>
                                </Avatar>
                                {host.isSpeaking ? (
                                    <motion.div 
                                        className="absolute -bottom-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center"
                                        animate={{ scale: [1, 1.2, 1] }}
                                        transition={{ repeat: Infinity, duration: 0.5 }}
                                    >
                                        <Mic className="w-3 h-3 text-white" />
                                    </motion.div>
                                ) : (
                                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-surface rounded-full flex items-center justify-center">
                                        <Mic className="w-3 h-3 text-white/50" />
                                    </div>
                                )}
                            </div>
                            <span className="text-white text-xs mt-1 truncate w-14 text-center">{host.name}</span>
                            <span className={`text-[10px] ${host.isSpeaking ? 'text-primary font-medium' : 'text-white/50'}`}>
                                {host.role}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Live Chat Section */}
            <div className="flex-1 flex flex-col min-h-0">
                <div className="px-4 py-2 border-b border-border flex-shrink-0">
                    <span className="text-white/60 text-xs uppercase tracking-wider">Live Chat</span>
                </div>
                
                <div 
                    ref={chatContainerRef}
                    className="flex-1 overflow-y-auto px-4 py-2 space-y-2"
                    data-testid="chat-container"
                >
                    <AnimatePresence initial={false}>
                        {messages.map((msg) => (
                            <motion.div
                                key={msg.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.2 }}
                                className={`flex items-start gap-2 ${msg.isOwn ? 'flex-row-reverse' : ''}`}
                            >
                                {!msg.isBetAnnouncement && (
                                    <Avatar className="w-6 h-6 flex-shrink-0">
                                        <AvatarImage src={msg.user.avatar} alt={msg.user.name} />
                                        <AvatarFallback className="bg-primary/20 text-primary text-xs">
                                            {msg.user.name.charAt(0)}
                                        </AvatarFallback>
                                    </Avatar>
                                )}
                                <div className={`flex flex-col ${msg.isOwn ? 'items-end' : 'items-start'} ${msg.isBetAnnouncement ? 'w-full' : ''}`}>
                                    {!msg.isBetAnnouncement && (
                                        <span className={`text-xs font-medium ${msg.isOwn ? 'text-primary' : 'text-white/60'}`}>
                                            {msg.user.name}
                                        </span>
                                    )}
                                    {msg.isBetAnnouncement ? (
                                        <div className="w-full py-2 px-3 bg-gradient-to-r from-yellow-500/20 to-primary/20 rounded-lg border border-yellow-500/30">
                                            <span className="text-yellow-400 text-sm font-medium">
                                                {msg.content}
                                            </span>
                                        </div>
                                    ) : (
                                        <span className={`text-sm ${msg.isOwn ? 'text-white bg-primary/30 px-2 py-1 rounded-lg' : 'text-white/90'}`}>
                                            {msg.content}
                                        </span>
                                    )}
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </div>
            </div>

            {/* Reaction Buttons */}
            <div className="px-4 py-2 border-t border-border flex items-center justify-center gap-3 flex-shrink-0 bg-surface">
                {[
                    { type: 'heart', icon: '❤️' },
                    { type: 'fire', icon: '🔥' },
                    { type: 'clap', icon: '👏' },
                    { type: 'goal', icon: '⚽' },
                ].map((reaction) => (
                    <button
                        key={reaction.type}
                        onClick={() => handleReaction(reaction.type)}
                        className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-2xl transition-all active:scale-90"
                        data-testid={`reaction-${reaction.type}`}
                    >
                        {reaction.icon}
                    </button>
                ))}
                
                {/* Make a Bet Button */}
                <button
                    onClick={openBettingModal}
                    className="h-12 px-4 rounded-full bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/50 flex items-center gap-2 transition-all active:scale-95"
                    data-testid="make-bet-button"
                >
                    <Coins className="w-5 h-5 text-yellow-400" />
                    <span className="text-yellow-400 font-semibold text-sm">Bet</span>
                </button>
            </div>

            {/* Chat Input */}
            <div className="px-4 py-3 border-t border-border flex-shrink-0 safe-bottom bg-black">
                <div className="flex gap-2">
                    <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Say something..."
                        className="flex-1 bg-surface border-border text-white placeholder:text-white/40"
                        data-testid="chat-input"
                    />
                    <Button
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim()}
                        className="bg-primary hover:bg-primary-hover px-4"
                        data-testid="send-chat-button"
                    >
                        <Send className="w-5 h-5" />
                    </Button>
                </div>
            </div>

            {/* Betting Modal */}
            <Dialog open={showBettingModal} onOpenChange={setShowBettingModal}>
                <DialogContent className="bg-card-bg border-border w-[calc(100%-2rem)] max-w-sm rounded-xl" data-testid="betting-modal">
                    <DialogHeader>
                        <DialogTitle className="text-white font-heading text-2xl flex items-center gap-2">
                            <Coins className="w-6 h-6 text-yellow-400" />
                            Betting Slip
                        </DialogTitle>
                    </DialogHeader>

                    {odds && (
                        <div className="space-y-4 mt-4">
                            {/* Match Info */}
                            <div className="text-center py-3 bg-surface rounded-xl">
                                <p className="text-white font-medium">{odds.home_team} vs {odds.away_team}</p>
                                <p className="text-white/50 text-xs mt-1">Live • {match?.minute}'</p>
                            </div>

                            {/* Odds Selection */}
                            <div className="space-y-2">
                                <p className="text-white/60 text-sm">Select Outcome:</p>
                                <div className="grid grid-cols-3 gap-2">
                                    <button
                                        onClick={() => setSelectedBet('home')}
                                        className={`p-3 rounded-xl border-2 transition-all ${
                                            selectedBet === 'home' 
                                                ? 'border-primary bg-primary/20' 
                                                : 'border-border bg-surface hover:border-white/30'
                                        }`}
                                        data-testid="bet-home"
                                    >
                                        <p className="text-white text-xs font-medium truncate">{odds.home_team}</p>
                                        <p className="text-primary font-heading text-xl font-bold mt-1">{odds.odds.home}</p>
                                    </button>
                                    <button
                                        onClick={() => setSelectedBet('draw')}
                                        className={`p-3 rounded-xl border-2 transition-all ${
                                            selectedBet === 'draw' 
                                                ? 'border-primary bg-primary/20' 
                                                : 'border-border bg-surface hover:border-white/30'
                                        }`}
                                        data-testid="bet-draw"
                                    >
                                        <p className="text-white text-xs font-medium">Draw</p>
                                        <p className="text-primary font-heading text-xl font-bold mt-1">{odds.odds.draw}</p>
                                    </button>
                                    <button
                                        onClick={() => setSelectedBet('away')}
                                        className={`p-3 rounded-xl border-2 transition-all ${
                                            selectedBet === 'away' 
                                                ? 'border-primary bg-primary/20' 
                                                : 'border-border bg-surface hover:border-white/30'
                                        }`}
                                        data-testid="bet-away"
                                    >
                                        <p className="text-white text-xs font-medium truncate">{odds.away_team}</p>
                                        <p className="text-primary font-heading text-xl font-bold mt-1">{odds.odds.away}</p>
                                    </button>
                                </div>
                            </div>

                            {/* Amount Input */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <p className="text-white/60 text-sm">Bet Amount:</p>
                                    <p className="text-white/60 text-xs">
                                        Balance: <span className="text-yellow-400 font-medium">{userCoins} coins</span>
                                    </p>
                                </div>
                                <div className="relative">
                                    <Coins className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-yellow-400" />
                                    <Input
                                        type="number"
                                        value={betAmount}
                                        onChange={(e) => setBetAmount(e.target.value)}
                                        placeholder="Enter amount (min. 10)"
                                        className="bg-surface border-border text-white pl-10 h-12"
                                        min="10"
                                        max={userCoins}
                                        data-testid="bet-amount-input"
                                    />
                                </div>
                                {/* Quick amounts */}
                                <div className="flex gap-2">
                                    {[50, 100, 250, 500].map((amount) => (
                                        <button
                                            key={amount}
                                            onClick={() => setBetAmount(String(Math.min(amount, userCoins)))}
                                            className="flex-1 py-2 bg-surface hover:bg-white/10 border border-border rounded-lg text-white/80 text-sm transition-colors"
                                        >
                                            {amount}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Potential Win */}
                            {selectedBet && betAmount && (
                                <div className="p-3 bg-primary/10 border border-primary/30 rounded-xl">
                                    <div className="flex items-center justify-between">
                                        <span className="text-white/60 text-sm">Potential Win:</span>
                                        <span className="text-primary font-heading text-xl font-bold">
                                            {calculatePotentialWin()} coins
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Place Bet Button */}
                            <Button
                                onClick={handlePlaceBet}
                                disabled={!selectedBet || !betAmount || parseInt(betAmount) < 10 || placingBet}
                                className="w-full bg-primary hover:bg-primary-hover text-white font-semibold h-12 rounded-full"
                                data-testid="place-bet-button"
                            >
                                {placingBet ? 'Placing Bet...' : 'Place Bet'}
                            </Button>
                        </div>
                    )}

                    {!odds && (
                        <div className="py-8 flex items-center justify-center">
                            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* CSS for speaking animation */}
            <style>{`
                .speaking-ring {
                    position: relative;
                }
                .speaking-ring::before {
                    content: '';
                    position: absolute;
                    inset: -4px;
                    border-radius: 50%;
                    border: 2px solid #169239;
                    animation: speakingPulse 1s ease-in-out infinite;
                }
                .speaking-ring::after {
                    content: '';
                    position: absolute;
                    inset: -8px;
                    border-radius: 50%;
                    border: 2px solid #169239;
                    opacity: 0.5;
                    animation: speakingPulse 1s ease-in-out infinite 0.2s;
                }
                @keyframes speakingPulse {
                    0%, 100% {
                        transform: scale(1);
                        opacity: 0.8;
                    }
                    50% {
                        transform: scale(1.15);
                        opacity: 0.2;
                    }
                }
            `}</style>
        </div>
    );
};

export default LiveMatchRoom;
